import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../api'
import { useAuth } from '../AuthContext'
import { isManagerRole } from '../shell/navigation'
import { rulePill } from './Pill'
import { ErrorState, errorMessage } from './ErrorState'
import { enumLabel, useI18n, useT } from '../i18n'
import type { TFunction } from '../i18n'
import type { CountryRuleRow, CountryCode } from '../types'

/**
 * Pays de la matrice. Le drapeau et la région sont neutres ; le nom vient du
 * catalogue, pour suivre la langue choisie.
 *
 * La région fourre-tout porte une clé, les deux unions monétaires gardant leur
 * sigle, qui ne se traduit pas.
 */
const ALL_COUNTRIES: { code: CountryCode; flag: string; region: string }[] = [
  { code: 'SN', flag: '\u{1F1F8}\u{1F1F3}', region: 'UEMOA' },
  { code: 'CI', flag: '\u{1F1E8}\u{1F1EE}', region: 'UEMOA' },
  { code: 'ML', flag: '\u{1F1F2}\u{1F1F1}', region: 'UEMOA' },
  { code: 'BF', flag: '\u{1F1E7}\u{1F1EB}', region: 'UEMOA' },
  { code: 'GN', flag: '\u{1F1EC}\u{1F1F3}', region: 'UEMOA' },
  { code: 'TG', flag: '\u{1F1F9}\u{1F1EC}', region: 'UEMOA' },
  { code: 'BJ', flag: '\u{1F1E7}\u{1F1EF}', region: 'UEMOA' },
  { code: 'NE', flag: '\u{1F1F3}\u{1F1EA}', region: 'UEMOA' },
  { code: 'CM', flag: '\u{1F1E8}\u{1F1F2}', region: 'CEMAC' },
  { code: 'GA', flag: '\u{1F1EC}\u{1F1E6}', region: 'CEMAC' },
  { code: 'GH', flag: '\u{1F1EC}\u{1F1ED}', region: 'OTHER' },
]

/** Nom d'une région dans le filtre. Les sigles ne se traduisent pas. */
function regionLabel(t: TFunction, region: string): string {
  return region === 'OTHER' ? t('rules.region.other') : region
}

const RULE_LEVELS = ['ALLOWED', 'CONDITIONAL', 'FLAGGED', 'PROHIBITED'] as const
type RuleLevel = (typeof RULE_LEVELS)[number]


interface EditableCellProps {
  value: string
  activity: string
  country: CountryCode
  hasRules: boolean
  /** Motif de lecture seule ; absent = modifiable. */
  readOnlyReason?: string
  onUpdate: (activity: string, country: CountryCode, level: RuleLevel) => void
}

function EditableCell({ value, activity, country, hasRules, readOnlyReason, onUpdate }: EditableCellProps) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (readOnlyReason) {
    return (
      <div className="rule-cell rule-cell-readonly" title={readOnlyReason}>
        {hasRules ? rulePill(value || '—') : <span className="pill pill-undefined">{t('rules.undefined')}</span>}
      </div>
    )
  }

  return (
    <div ref={ref} className="rule-cell" style={{ position: 'relative' }}>
      <button
        className="rule-cell-btn"
        onClick={() => setOpen(!open)}
        title={
          hasRules
            ? t('rules.editCell', { activity, country })
            : t('rules.defineCell', { country })
        }
      >
        {hasRules ? rulePill(value || '—') : (
          <span className="pill pill-undefined">{t('rules.undefined')}</span>
        )}
      </button>
      {open && (
        <div className="rule-dropdown">
          {RULE_LEVELS.map((level) => (
            <button
              key={level}
              className={`rule-dropdown-item ${value === level ? 'rule-dropdown-active' : ''}`}
              onClick={() => {
                onUpdate(activity, country, level)
                setOpen(false)
              }}
            >
              {rulePill(level)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function RulesMatrix() {
  const { user, effectiveCountries } = useAuth()
  const { t } = useI18n()
  const canManage = isManagerRole(user)
  // Les règles sont communes à la plateforme : un administrateur ne modifie que ses pays.
  const readOnlyReason = (code: CountryCode): string | undefined => {
    if (!canManage) return t('rules.readOnlyRole')
    if (effectiveCountries !== null && !effectiveCountries.includes(code)) return t('rules.readOnlyScope')
    return undefined
  }
  const [rows, setRows] = useState<CountryRuleRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [pendingChanges, setPendingChanges] = useState<number>(0)
  const [toast, setToast] = useState<{ message: string; type: 'ok' | 'error' } | null>(null)
  const [selectedCountries, setSelectedCountries] = useState<Set<CountryCode>>(
    new Set(ALL_COUNTRIES.map(c => c.code))
  )
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterRegion, setFilterRegion] = useState<string>('all')
  const filterRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      setRows(await api.getRulesMatrix())
      setError(null)
    } catch (err: unknown) {
      setRows([])
      setError(errorMessage(err))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [toast])

  useEffect(() => {
    if (!filterOpen) return
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [filterOpen])

  const handleUpdate = useCallback(
    async (activity: string, country: CountryCode, level: RuleLevel) => {
      // Mise a jour optimiste, avec restauration exacte si le serveur refuse.
      let snapshot: CountryRuleRow[] = []
      setRows((prev) => {
        snapshot = prev
        return prev.map((row) =>
          row.activity === activity
            ? { ...row, rules: { ...row.rules, [country]: level } }
            : row,
        )
      })
      setPendingChanges((n) => n + 1)

      try {
        const updated = await api.updateRule(activity, country, level)
        setRows(updated)
        setToast({
          message: t('rules.updated', {
            activity: enumLabel(t, 'activity', activity, activity),
            country,
            level: enumLabel(t, 'rule', level, level),
          }),
          type: 'ok',
        })
      } catch (err: unknown) {
        setRows(snapshot) // rollback : la regle n'a PAS ete enregistree
        setToast({ message: t('rules.saveFailed', { error: errorMessage(err) }), type: 'error' })
      } finally {
        setPendingChanges((n) => Math.max(0, n - 1))
      }
    },
    [t],
  )

  const toggleCountry = (code: CountryCode) => {
    setSelectedCountries(prev => {
      const next = new Set(prev)
      if (next.has(code)) {
        if (next.size > 1) next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })
  }

  const selectRegion = (region: string) => {
    setFilterRegion(region)
    if (region === 'all') {
      setSelectedCountries(new Set(ALL_COUNTRIES.map(c => c.code)))
    } else {
      setSelectedCountries(new Set(
        ALL_COUNTRIES.filter(c => c.region === region).map(c => c.code)
      ))
    }
  }

  const visibleCountries = ALL_COUNTRIES.filter(c => selectedCountries.has(c.code))
  const countriesWithRules = new Set(rows.length > 0 ? Object.keys(rows[0].rules) : [])

  return (
    <div className="table-card">
      <div className="table-header">
        <div>
          <h2>{t('rules.matrixTitle')}</h2>
          <span className="td-muted">
            {canManage ? t('rules.editHint') : t('rules.readOnlyHint')}
          </span>
        </div>
        <div className="rules-header-actions">
          {pendingChanges > 0 && (
            <span className="pill pill-warning">{t('rules.pending', { n: pendingChanges })}</span>
          )}

          <div ref={filterRef} className="rules-filter-wrap">
            <button
              className="rules-filter-btn"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              {t('rules.countryFilter', { shown: visibleCountries.length, total: ALL_COUNTRIES.length })}
              <span className="rules-filter-chevron">{filterOpen ? '▲' : '▼'}</span>
            </button>

            {filterOpen && (
              <div className="rules-filter-dropdown">
                <div className="rules-filter-regions">
                  {['all', 'UEMOA', 'CEMAC', 'OTHER'].map(r => (
                    <button
                      key={r}
                      onClick={() => selectRegion(r)}
                      className={`rules-filter-region${filterRegion === r ? ' rules-filter-region-active' : ''}`}
                    >
                      {r === 'all' ? t('rules.region.all') : regionLabel(t, r)}
                    </button>
                  ))}
                </div>
                {ALL_COUNTRIES.map(c => (
                  <label key={c.code} className="rules-filter-item">
                    <input
                      type="checkbox"
                      checked={selectedCountries.has(c.code)}
                      onChange={() => toggleCountry(c.code)}
                    />
                    <span>{c.flag}</span>
                    <span className="rules-filter-name">{enumLabel(t, 'country', c.code, c.code)}</span>
                    {!countriesWithRules.has(c.code) && (
                      <span className="rules-no-rules-badge">{t('rules.noRules')}</span>
                    )}
                    <span className="rules-filter-region-label">{regionLabel(t, c.region)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`rules-toast ${toast.type === 'ok' ? 'rules-toast-ok' : 'rules-toast-error'}`}>
          {toast.message}
        </div>
      )}

      {error && <ErrorState message={error} onRetry={load} compact />}

      <div className="rules-scroll" hidden={!!error}>
        <table className="rules-table">
          <thead>
            <tr>
              <th className="rules-sticky-col">{t('rules.activity')}</th>
              {visibleCountries.map((c) => (
                <th key={c.code} className={countriesWithRules.has(c.code) ? '' : 'rules-country-unconfigured'}>
                  {c.flag} {enumLabel(t, 'country', c.code, c.code)}
                  {!countriesWithRules.has(c.code) && (
                    <div className="rules-unconfigured-hint">{t('rules.toConfigure')}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.activity}>
                {/* Le serveur envoie un libellé français : le catalogue prend le
                    dessus quand il connaît l'activité, pour que l'anglais le soit. */}
                <td className="rules-sticky-col merchant-name">{enumLabel(t, 'activity', row.activity, row.activityLabel)}</td>
                {visibleCountries.map((c) => (
                  <td key={c.code} className="rules-cell-td">
                    <EditableCell
                      value={row.rules[c.code] ?? ''}
                      activity={row.activity}
                      country={c.code}
                      hasRules={countriesWithRules.has(c.code)}
                      readOnlyReason={readOnlyReason(c.code)}
                      onUpdate={handleUpdate}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rules-legend">
        <span className="rules-legend-title">{t('rules.legend')}</span>
        {rulePill('ALLOWED')} <span className="td-muted">{t('rules.legend.allowed')}</span>
        {rulePill('CONDITIONAL')} <span className="td-muted">{t('rules.legend.conditional')}</span>
        {rulePill('FLAGGED')} <span className="td-muted">{t('rules.legend.flagged')}</span>
        {rulePill('PROHIBITED')} <span className="td-muted">{t('rules.legend.prohibited')}</span>
        <span className="pill pill-undefined">{t('rules.undefined')}</span>
        <span className="td-muted">{t('rules.notConfigured')}</span>
      </div>
    </div>
  )
}
